import React from "react";
import { createRoot } from "react-dom/client";
import type { Root } from "react-dom/client";
import ReportBreakModalHost from "../components/ReportBreakModalHost";
import { verifyClassLink } from "../api/reports";

const REPORT_HOST_ID = "breakscampaign-report-root";

let reportRoot: Root | null = null;
let reportToken = "";

/** Nyckeln ligger i fragmentet (#key=), som webbläsaren aldrig skickar till servern. */
function readKeyFromHash(): string {
  return (
    new URLSearchParams(window.location.hash.slice(1)).get("key")?.trim() ?? ""
  );
}

/**
 * Kontrollerar nyckeln i fragmentet och visar i så fall rapporteringen. Kan köras
 * flera gånger — t.ex. när hashen ändras utan att sidan laddas om.
 */
async function verifyAndMountReporting(): Promise<void> {
  const token = readKeyFromHash();

  if (token === "") {
    return;
  }

  // Samma nyckel är redan verifierad och monterad.
  if (token === reportToken) {
    return;
  }

  const config = document.querySelector<HTMLElement>(
    "[data-breaks-report-config]",
  );
  const invalid = document.querySelector<HTMLElement>(
    "[data-breaks-report-invalid]",
  );

  let valid = false;

  if (config) {
    try {
      valid = await verifyClassLink(config.dataset.reportSlug ?? "", token);
    } catch {
      // Nätverksfel säger inget om nyckeln, så varningen om ogiltig länk visas inte.
      return;
    }
  }

  // Hashen kan ha ändrats medan nyckeln verifierades — då gäller den nyare körningen.
  if (token !== readKeyFromHash()) {
    return;
  }

  if (!config || !valid) {
    if (invalid) invalid.hidden = false;
    return;
  }

  if (invalid) invalid.hidden = true;

  // Nyckeln är giltig och anmälan bekräftad, men rapporteringen är pausad eller har inte
  // öppnat än. Servern visar själv meddelandet, så panelen och knapparna hålls dolda.
  if (config.dataset.reportClosed === "true") {
    return;
  }

  document
    .querySelectorAll<HTMLElement>("[data-breaks-report]")
    .forEach((button) => (button.hidden = false));
  config.hidden = false;

  reportToken = token;

  if (!reportRoot) {
    const host = document.createElement("div");
    host.id = REPORT_HOST_ID;
    document.body.appendChild(host);
    reportRoot = createRoot(host);
  }

  reportRoot.render(React.createElement(ReportBreakModalHost, { token }));
}

/**
 * Rapporteringen på klassidan. Sidan renderas likadant för alla, och rapportknapparna
 * ligger dolda tills nyckeln i fragmentet godkänts av API:et för just den här klassen.
 */
export function initBreakReporting(): void {
  void verifyAndMountReporting();

  // Att klistra in samma sida med #key=... laddar inte om sidan, bara hashen ändras.
  window.addEventListener("hashchange", () => void verifyAndMountReporting());
}
